import React from "react";
import EbDialog from "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/_shared/EbDialog";
import "../../ExecutionBlueprint/ExecutionBlueprintReview/InfoDialog.scss";

// ═════════════════════════════════════════════════════════════════════════════
// The confirmation in front of "Start Live Review" — the coach's last stop
// before the joint session begins. Built on the same shared `EbDialog` as the
// Execution Blueprint's equivalent.
//
// ⚠️ NO STEPPER. EXW's version is a numbered three-stage rail because its live
// review genuinely runs to three screens — Blueprint Review, then WIG
// Connections, then Shortlisted WIGs for Year 1 — and its own comment says the
// rail exists so a coach "needs to know it runs to three stages before they
// start, not discover the second one halfway through".
//
// Annual OKR's live review is ONE session on ONE screen: §7 describes a single
// room where both parties work through the Objectives and the remarks, ending
// at the lock. There is no second screen after it. A numbered rail would
// therefore be a sequence UI promising stages that do not exist — the precise
// failure EXW's own comments warn about twice — and even a three-point rail
// (which this file had until 2026-09-21) read as "three screens to get
// through" rather than "three things to know".
//
// So the body is prose and a plain list: what the session is, and what has to
// be cleared before it can end. It reuses `InfoDialog.scss`'s `info-steps`, the
// same unnumbered list the "How review works" explainer uses.
//
// It is still a dialog rather than a bare confirm, because the thing worth
// knowing before starting is the gate on finishing: every remark discussed, or
// the lock refuses (§7.5).
// ═════════════════════════════════════════════════════════════════════════════

const POINTS = [
  "You and the CEO see the same objectives, and either of you can add, edit or remove one as you talk.",
  "Every comment and every flagged objective has to be marked discussed — that is the gate on locking.",
  "Locking is final: it ends the session for both of you and closes the OKRs for the year.",
];

const PlayIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M10.2 8.9v6.2l5.1-3.1-5.1-3.1Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * @param onNext      Starts the live review (§6.8). The one-way action.
 * @param onBack      Dismisses. Also what Escape and the backdrop call.
 * @param nextLoading The POST is in flight: spins the primary and locks
 *                    dismissal, so the dialog can't be closed out from under a
 *                    request that is about to change the run's state.
 */
const LiveReviewStepperModal = ({
  onNext,
  onBack,
  nextLabel = "Start live review",
  backLabel = "Go back",
  nextLoading = false,
}) => (
  <EbDialog
    isOpen
    size="lg"
    tone="primary"
    icon={<PlayIcon />}
    title="Start the live review?"
    // JK copy review O12: "a live session that we keep short… to finalize the
    // annual OKRs" / "discuss, align and commit".
    subtitle="A short session with the CEO and their leadership team to discuss, agree and lock this year's Company OKRs."
    busy={nextLoading}
    onClose={onBack}
    align="between"
    actions={[
      { label: backLabel, variant: "ghost", onClick: onBack },
      {
        label: nextLabel,
        loadingLabel: "Starting…",
        variant: "primary",
        onClick: onNext,
        loading: nextLoading,
        autoFocus: true,
      },
    ]}
  >
    <p>
      The CEO and their leadership team join you on the same board. There is no
      stage after this one — you finish by locking the OKRs together.
    </p>

    <ol className="info-steps">
      {POINTS.map((point) => (
        <li key={point}>{point}</li>
      ))}
    </ol>
  </EbDialog>
);

export default LiveReviewStepperModal;
